import { lazy, Suspense, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, HeartHandshake, Eye, Sparkles, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { useBrand } from "../context/BrandContext";
import { useAuth } from "../context/AuthContext";
import { PricingSection } from "../components/PricingSection";
import { GuestDemo } from "../components/landing/GuestDemo";
import { DEFAULT_TESTIMONIALS, TESTIMONIAL_PHOTOS } from "../config/galaxyPhotos";

// The cinematic 3D Growth Hub is code-split so the landing page paints fast
// and the Three.js bundle only downloads when this page renders.
const GrowthHub3D = lazy(() => import("./GrowthHub3D"));

const HERO_IMG =
  "https://images.unsplash.com/photo-1714646792960-3c700930799b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxoYXBweSUyMHBhcmVudCUyMGNoaWxkJTIwdGFibGV0JTIwbGVhcm5pbmd8ZW58MHx8fHwxNzg2MTYzNTExfDA&ixlib=rb-4.1.0&q=85";

const PILLARS = [
  {
    icon: ShieldCheck,
    title: "Safety by design",
    text: "Nothing reaches your child without your verifiable consent. Kill switches and audit trails are built in from day one.",
  },
  {
    icon: HeartHandshake,
    title: "You stay in charge",
    text: "Granular consent controls. Review, change, or withdraw permission any time — it takes effect immediately.",
  },
  {
    icon: Eye,
    title: "Fully transparent",
    text: "A family activity ledger shows every consent decision and every access to your child's experience.",
  },
  {
    icon: Sparkles,
    title: "Support, not diagnosis",
    text: "Educational practice and guided encouragement. Never medical advice, and never a replacement for professionals.",
  },
];

export default function Landing() {
  const brand = useBrand();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [demo, setDemo] = useState(null);
  // BETA50 ribbon — clickable (auto-applies the code at checkout), dismissible,
  // with a gentle countdown; text + end date are admin-configurable and the
  // ribbon hides itself automatically once the offer ends.
  const [offer, setOffer] = useState({ text: "50% off your first month", text_b: "", end_date: "2026-09-30", enabled: true });
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/landing/offer`)
      .then((r) => r.json())
      .then((d) => setOffer(d))
      .catch(() => {});
  }, []);
  // A/B copy: each browser is assigned a sticky variant; B only runs when a
  // variant-B text exists. Ribbon clicks are counted per variant.
  const [variant] = useState(() => {
    const saved = localStorage.getItem("offerVariant");
    if (saved === "a" || saved === "b") return saved;
    const v = Math.random() < 0.5 ? "a" : "b";
    localStorage.setItem("offerVariant", v);
    return v;
  });
  const offerText = variant === "b" && offer.text_b ? offer.text_b : offer.text;
  const offerEnd = new Date(`${offer.end_date}T23:59:59Z`);
  const daysLeft = Math.ceil((offerEnd - Date.now()) / 86400000);
  const endLabel = offerEnd.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const [bannerOpen, setBannerOpen] = useState(
    () => localStorage.getItem("beta50BannerDismissed") !== "1");
  const dismissBanner = (e) => {
    e.stopPropagation();
    localStorage.setItem("beta50BannerDismissed", "1");
    setBannerOpen(false);
  };
  const applyBeta50 = () => {
    localStorage.setItem("applyBeta50", "1");
    window.dispatchEvent(new Event("beta50-applied"));
    // count the click for its A/B variant (fire-and-forget)
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/landing/offer-click`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ variant }),
    }).catch(() => {});
    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
  };
  // Admin-edited testimonials (Testimonial Manager) merged over defaults.
  const [tOverrides, setTOverrides] = useState({});
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/landing/testimonials`)
      .then((r) => r.json())
      .then((d) => setTOverrides(d.testimonials || {}))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#080C14] text-white">
      {bannerOpen && offer.enabled && daysLeft > 0 && (
        <div role="button" tabIndex={0}
          onClick={applyBeta50}
          onKeyDown={(e) => e.key === "Enter" && applyBeta50()}
          className="relative z-20 cursor-pointer bg-gradient-to-r from-[#0d1626] via-[#1a2438] to-[#0d1626] border-b border-amber-400/25 transition-colors hover:border-amber-400/50"
          data-testid="beta50-banner">
          <div className="max-w-6xl mx-auto px-6 py-2 flex items-center justify-center gap-2 text-center">
            <Sparkles className="h-4 w-4 text-amber-300 shrink-0" aria-hidden="true" />
            <p className="text-xs sm:text-sm text-amber-100 whitespace-nowrap overflow-hidden text-ellipsis">
              {/* compact one-liner on phones; full copy with countdown on larger screens */}
              <span className="sm:hidden">
                <span className="font-extrabold text-amber-300">{offerText}</span> — code{" "}
                <span className="font-mono font-bold text-amber-200">BETA50</span> · {daysLeft}d left
              </span>
              <span className="hidden sm:inline">
                Limited beta offer — <span className="font-extrabold text-amber-300">{offerText}</span> with
                code{" "}
                <span className="rounded-md bg-amber-400/15 border border-amber-400/30 px-1.5 py-0.5 font-mono font-bold text-amber-200">BETA50</span>
                <span className="text-amber-200/80"> · ends {endLabel} — only {daysLeft} {daysLeft === 1 ? "day" : "days"} left. Tap to claim</span>
              </span>
            </p>
            <button onClick={dismissBanner} aria-label="Dismiss offer banner"
              className="absolute right-4 text-slate-400 hover:text-white transition-colors"
              data-testid="beta50-banner-dismiss">
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
      <header className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <span className="text-2xl font-extrabold font-display text-white" data-testid="landing-brand-name">
          {brand.platform_name}
        </span>
        <div className="flex items-center gap-3">
          {user ? (
            <Button asChild className="rounded-full px-6" data-testid="landing-dashboard-button">
              <Link to="/dashboard">My dashboard</Link>
            </Button>
          ) : (
            <Button asChild className="rounded-full px-6 bg-[#FB7185] hover:bg-[#F43F5E] text-white font-bold" data-testid="landing-signin-button">
              <Link to="/auth">Sign in</Link>
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-none">
        {/* Immersive hero — STRICT three-zone vertical layout (no overlap):
            TOP 25%  = headline + "Teach, Don't Cheat" sub-headline
            MIDDLE 50% = 3D Learning Galaxy (Pip + 6 nodes, scaled down)
            BOTTOM 25% = CTA buttons + disclaimer                       */}
        <section className="relative flex flex-col overflow-hidden"
          style={{
            height: "calc(100vh - 88px)",
            minHeight: 760,
            background: "radial-gradient(circle at 50% 46%, #16233d 0%, #080C14 70%)",
          }}
          data-testid="landing-hero">

          {/* ===== TOP ZONE (25%) — headline only ===== */}
          <div className="relative z-10 basis-1/4 shrink-0 flex flex-col items-center justify-center text-center px-6 gap-3"
            data-testid="hero-top-zone">
            <h1 className="max-w-4xl text-3xl sm:text-4xl lg:text-5xl font-extrabold font-display leading-[1.08] text-white"
              style={{ textShadow: "0 4px 30px rgba(8, 12, 20, 0.9)" }}>
              Turn Daily Screen Time into Speech Mastery, Math Confidence &amp; Real-World Growth.
            </h1>
            <p className="text-base md:text-lg font-bold text-[#F59E0B]"
              style={{ textShadow: "0 0 18px rgba(245, 158, 11, 0.55)" }}>
              Teach, Don't Cheat — Pip coaches kids to think, speak and solve for themselves.
            </p>
          </div>

          {/* ===== MIDDLE ZONE (50%) — the 3D Galaxy Hub, fully contained ===== */}
          <div className="relative basis-1/2 min-h-0" data-testid="hero-middle-zone">
            <Suspense fallback={
              <img src={HERO_IMG} alt="Parent and child learning together on a tablet"
                className="object-cover w-full h-full opacity-60" />
            }>
              <GrowthHub3D fullBleed
                onNavigate={(to) => {
                  if (user) return navigate(to);
                  // Guest Demo: visitors try a one-tap sample round first
                  if (to === "/math-hub" || to === "/math-boss") return setDemo("math");
                  if (to === "/dashboard") return setDemo("speech");
                  navigate("/auth?mode=register");
                }} />
            </Suspense>
          </div>

          {/* ===== BOTTOM ZONE (25%) — CTAs + disclaimer ===== */}
          <div className="relative z-10 basis-1/4 shrink-0 flex flex-col items-center justify-center gap-4 px-6"
            data-testid="hero-bottom-zone">
            <div className="flex flex-wrap gap-4 justify-center">
              <Button asChild size="lg"
                className="rounded-full px-8 h-14 text-base bg-[#10B981] text-white hover:bg-[#0EA371] transition-shadow hover:shadow-[0_0_28px_rgba(16,185,129,0.55)]"
                data-testid="landing-get-started-button">
                <Link to={user ? "/dashboard" : "/auth?mode=register"}>Get Started</Link>
              </Button>
              <Button asChild size="lg" variant="ghost"
                className="rounded-full px-8 h-14 text-base text-white border border-white/25 hover:bg-white/10 hover:text-white"
                data-testid="landing-learn-more-button">
                <a href="#pillars">How we protect your child</a>
              </Button>
            </div>
            <p className="text-sm text-slate-400" style={{ textShadow: "0 2px 12px rgba(8,12,20,0.9)" }}>
              Educational support only — not diagnosis, screening, or medical advice.
            </p>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-6 pt-16">

        {/* parent social proof */}
        <section className="pb-16" data-testid="landing-testimonials">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {DEFAULT_TESTIMONIALS.map((t) => {
              const o = tOverrides[t.slot] || {};
              const quote = o.quote || t.quote;
              const name = o.name || t.name;
              const role = o.role || t.role;
              const photo = o.photo || TESTIMONIAL_PHOTOS[t.name];
              return (
                <figure key={t.slot}
                  className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-6 space-y-3"
                  data-testid={`testimonial-${t.slot}`}>
                  <blockquote className="text-sm text-[#94A3B8] leading-relaxed">"{quote}"</blockquote>
                  <figcaption className="flex items-center gap-3">
                    <img src={photo} alt={`${name} — parent`}
                      loading="lazy" width="36" height="36"
                      className="h-9 w-9 rounded-full object-cover border border-[#10B981]/40 shrink-0" />
                    <span>
                      <span className="block text-sm font-bold text-white">{name}</span>
                      <span className="block text-xs text-slate-500">{role}</span>
                    </span>
                  </figcaption>
                </figure>
              );
            })}
            {/* COPPA trust badge card (mock-up: frosted card, cyan shield) */}
            <div className="rounded-2xl border border-cyan-400/25 bg-white/5 backdrop-blur-md p-6 flex items-center gap-4"
              data-testid="trust-coppa-card">
              <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-cyan-400/10 border border-cyan-400/40 text-cyan-300">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 2 L20 6 V11 C20 16.5 16.5 20.8 12 22 C7.5 20.8 4 16.5 4 11 V6 Z" />
                  <path d="M 8.5 12 L 11 14.5 L 15.5 9.5" />
                </svg>
              </span>
              <span>
                <span className="block text-sm font-extrabold text-white">COPPA Certified</span>
                <span className="block text-xs text-[#94A3B8]">Per-child consent &amp; data isolation.</span>
              </span>
            </div>
          </div>
        </section>

        {/* pricing — monthly/annual toggle + professional tier */}
        <PricingSection />

        <section id="pillars" className="py-16 border-t border-white/10">
          <h2 className="text-base md:text-lg font-bold uppercase tracking-widest text-[#F59E0B] mb-10">
            Built on trust, before anything else
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PILLARS.map((p) => (
              <div key={p.title} className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-7 space-y-4">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#10B981]/15 border border-[#10B981]/40 text-[#10B981]">
                  <p.icon className="h-6 w-6" aria-hidden="true" />
                </span>
                <h3 className="text-lg font-bold font-display text-white">{p.title}</h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </section>
        </div>
      </main>

      <footer className="max-w-6xl mx-auto px-6 py-10 text-sm text-slate-500 border-t border-white/10">
        {/* full-width Stripe trust bar (mock-up footer) */}
        <div className="flex flex-wrap items-center gap-3 mb-4 pb-8" data-testid="footer-trust-row">
          <svg viewBox="0 0 60 25" className="h-4 w-10 shrink-0" role="img" aria-label="Stripe" data-testid="footer-stripe-signal">
              <path fill="#635BFF" d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 0 0-4.1-1.06c-.86 0-1.44.25-1.44.9 0 1.85 6.29.97 6.29 5.88z" />
            </svg>
          <p className="text-xs text-[#94A3B8] m-0">
            Payments processed securely by Stripe. Every child stays protected by your
            per-child consent choices.
          </p>
        </div>
        {brand.footer_text
          ? brand.footer_text
          : <>{brand.platform_name} — {(brand.tagline || "").replace(/\.+$/, "")}. Educational support platform. Not a medical device.</>}
        {(brand.privacy_policy_url || brand.terms_url) ? (
          <span className="ml-2">
            {brand.privacy_policy_url && <a href={brand.privacy_policy_url} className="underline mr-2" data-testid="footer-privacy-link">Privacy</a>}
            {brand.terms_url && <a href={brand.terms_url} className="underline" data-testid="footer-terms-link">Terms</a>}
          </span>
        ) : (
          <span className="ml-2">
            <Link to="/privacy" className="underline mr-2" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/terms" className="underline" data-testid="footer-terms-link">Terms</Link>
          </span>
        )}
      </footer>
      <GuestDemo key={demo || "closed"} mode={demo} onClose={() => setDemo(null)} />
    </div>
  );
}
